<template>
    <EditResumeImportUploadFileV2
        v-if="isResumeImportEditionV2"
        class="edit-resume-import-step-upload-file"
        :rule-dropzone-file-formats="ruleDropzoneFileFormats"
        :rule-dropzone-max-file-size="ruleDropzoneMaxFileSize"
        :invalid="showFileRequiredError"
        @update:model-value="(file) => (uploadedResumeFile = file)"
    >
        <template #cta>
            <div class="edit-resume-import-step-upload-file__cta">
                <VJoyButton
                    data-testid="profile-edition-experience-resume-import-cta-import"
                    :label="t('profile.experience.funnel.step.upload.cta.import')"
                    variant="main"
                    :loading="isStepLoading"
                    @click="handleImportClick"
                />
            </div>
        </template>
    </EditResumeImportUploadFileV2>
    <ResumeImportUploadFile
        v-else
        class="edit-resume-import-step-upload-file"
        :rule-dropzone-file-formats="ruleDropzoneFileFormats"
        :rule-dropzone-max-file-size="ruleDropzoneMaxFileSize"
        @update:model-value="(file) => (uploadedResumeFile = file)"
    />
</template>
<script setup lang="ts">
    import {computed, onMounted, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useDataTracking, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ResumeImportUploadFile from '~/components/resume-import/ResumeImportUploadFile.vue';
    import type {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {ResumeImportStep, TrackingEvents} from '~/config/TrackingEvents';
    import {useDefineFunnelNextStepHandler} from '~/features/resume-import/composable/useDefineFunnelNextStepHandler';
    import {resumeImportUploadRules} from '~/features/resume-import/config/ResumeImportRules';
    import {useResumeImportStore} from '~/features/resume-import/store/useResumeImportStore';
    import EditResumeImportUploadFileV2 from '~/features/resume-import/ui/EditResumeImportUploadFileV2.vue';

    const {t} = useTranslation();
    const isResumeImportEditionV2 = useFeatureFlag('resume-import-edition-v2');

    const resumeImportStore = useResumeImportStore();
    const {resumeFileUpload} = resumeImportStore;
    const {isFunnelCtaNextEnabled} = storeToRefs(resumeImportStore);

    const isStepLoading = ref<boolean>(false);
    const uploadedResumeFile = ref<File | undefined>();
    const hasAttemptedImport = ref(false);
    const showFileRequiredError = computed(() => hasAttemptedImport.value && !uploadedResumeFile.value);

    const ruleDropzoneFileFormats = resumeImportUploadRules.fileFormats;
    const ruleDropzoneMaxFileSize = resumeImportUploadRules.maxFileSize;

    const emit = defineEmits<{
        'entry-point:reset': [];
        'funnel-step:next-step': [];
    }>();

    const props = defineProps<{
        entryPoint: EditionOpenedEntryPoint;
    }>();

    // Composable
    useDefineFunnelNextStepHandler(stepNextAction);
    const {track} = useDataTracking();

    onMounted(() => {
        clear();
        track(TrackingEvents.RESUME_IMPORT_FUNNEL, {funnel_step: ResumeImportStep.UPLOAD_RESUME, entry_point: props.entryPoint});
        emit('entry-point:reset');
    });

    watch(uploadedResumeFile, () => {
        isFunnelCtaNextEnabled.value = !!uploadedResumeFile.value;
    });

    async function stepNextAction(): Promise<void> {
        try {
            isStepLoading.value = true;
            if (uploadedResumeFile.value) {
                await resumeFileUpload(uploadedResumeFile.value);
                clear();
            }
        } finally {
            isStepLoading.value = false;
        }
    }
    function handleImportClick(): void {
        hasAttemptedImport.value = true;
        if (uploadedResumeFile.value) {
            emit('funnel-step:next-step');
        }
    }

    function clear(): void {
        isFunnelCtaNextEnabled.value = false;
        hasAttemptedImport.value = false;
    }
</script>
<style lang="scss" scoped>
    .edit-resume-import-step-upload-file {
        &__cta {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-end;
            gap: var(--joy-core-spacing-3);
        }
    }
</style>
